import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SSOSecuritySettings {
  ssoBypass2FA: boolean;
  ssoBypassUsers: string[];
  loading: boolean;
}

/**
 * Hook to fetch SSO security settings from system_settings table.
 */
export function useSSOSecuritySettings(): SSOSecuritySettings {
  const [ssoBypass2FA, setSsoBypass2FA] = useState(true);
  const [ssoBypassUsers, setSsoBypassUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from("system_settings")
          .select("key, value")
          .in("key", ["sso_bypass_2fa", "sso_bypass_2fa_users"]);

        if (error) {
          console.error("[useSSOSecuritySettings] Error fetching settings:", error);
        }

        if (data) {
          for (const setting of data) {
            if (setting.key === "sso_bypass_2fa" && setting.value !== undefined) {
              setSsoBypass2FA(setting.value === true || setting.value === "true");
            }
            if (setting.key === "sso_bypass_2fa_users" && Array.isArray(setting.value)) {
              setSsoBypassUsers(setting.value as string[]);
            }
          }
        }
      } catch (error) {
        console.error("[useSSOSecuritySettings] Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();

    const channel = supabase
      .channel("sso_security_settings")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "system_settings",
        },
        (payload) => {
          const newData = payload.new as { key: string; value: unknown } | null;
          if (newData && typeof newData === "object" && "key" in newData && "value" in newData) {
            const { key, value } = newData;
            if (key === "sso_bypass_2fa") {
              setSsoBypass2FA(value === true || value === "true");
            }
            if (key === "sso_bypass_2fa_users" && Array.isArray(value)) {
              setSsoBypassUsers(value);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { ssoBypass2FA, ssoBypassUsers, loading };
}
